import { useState, useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { motion, AnimatePresence } from 'framer-motion';
import {
  Droplets,
  Wifi,
  ArrowRight,
  Leaf,
  CreditCard,
  Cloud,
  Truck,
  Store,
  Globe,
  Settings,
  MapPin,
  Mail,
  Menu,
  X,
  TrendingUp,
  Flame,
  Shield,
  Smartphone,
  Clock,
  Recycle,
  Wind,
  Handshake,
  Building2,
  Cpu,
  Phone,
  Users,
} from 'lucide-react';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Textarea } from './components/ui/textarea';
import { Label } from './components/ui/label';
import { toast } from 'sonner';
import { AfricaMap } from './components/AfricaMap';
import WorldMap from 'react-svg-worldmap';

const BACKEND_URL = '.';
const LOGO_URL = "images/logo.png";
const HERO_BG = "images/hero-bg.jpg";
const TEAM_PHOTO = "images/team-photo.jpg";

const storeImages = [
  "images/gallery-1.jpg",
  "images/gallery-2.jpg",
  "images/gallery-3.jpg",
  "images/gallery-4.jpg",
  "images/gallery-5.jpg",
];
const typingPhrases = [
  "Clean Water, Built for Africa",
  "Cashless Digital Payments",
  "Plastic-free Hydration",
  "Smart Refill Technology",
  "24/7 Water Access"
];

const navLinks = [
{ name: 'Home', id: 'home' },
{ name: 'About Us', id: 'story' },
{ name: 'Technology', id: 'technology' },
{ name: 'JoinTheNetwork', id: 'join' },
{ name: 'Our Markets', id: 'markets' },
{ name: 'Contact', id: 'contact' }];


const stats = [
{ value: '200K+', label: 'People served' },
{ value: '50+', label: 'Smart stores' },
{ value: '1M+', label: 'Bottles saved' },
{ value: '3', label: 'Countries' }];


const techSteps = [
  {
    icon: Droplets,
    num: '01',
    tab: 'Water Source',
    title: 'Water Source',
    short: 'Municipal, borehole, or surface water',
    details: 'Ecowater connects to locally available water sources, including municipal supply, boreholes, or surface water. The system is designed to work with any raw water input, making it deployable anywhere across the continent.',
    highlights: ['Municipal water', 'Borehole supply', 'Surface water compatible']
  },
  {
    icon: Shield,
    num: '02',
    tab: 'Purification',
    title: 'Point-of-Use Purification',
    short: 'Water purified where it\'s consumed',
    details: 'High-efficiency filtration at each store eliminates the need for bottling factories, plastic packaging, and long-distance transport, ensuring consistent quality at lower cost.',
    highlights: ['No bottling factories', 'No plastic packaging', 'No long-distance transport']
  },
  {
    icon: Wifi,
    num: '03',
    tab: 'Smart Refill Station',
    title: 'Smart Refill Station (IoT)',
    short: 'IoT-enabled, automated, 24/7',
    details: 'Each store is equipped with sensors and automated controls that monitor water quality, system performance, dispensing activity, and machine health, running around the clock with minimal staffing.',
    highlights: ['Water quality monitoring', 'Automated operations', '24/7 uptime']
  },
  {
    icon: CreditCard,
    num: '04',
    tab: 'Cashless PayGo',
    title: 'Cashless PayGo Access',
    short: 'Digital wallets, instant refills',
    details: 'Customers load digital wallets and refill water instantly. Once funded, they get unlimited access anytime, anywhere across the Ecowater network.',
    highlights: ['Digital wallets', 'Prepaid balances', 'Unlimited refills, anytime, anywhere']
  },
  {
    icon: Cloud,
    num: '05',
    tab: 'Cloud Monitoring',
    title: 'Cloud Monitoring Platform',
    short: 'Data-driven water infrastructure',
    details: 'A centralized cloud system manages payments, machine monitoring, water quality data, operational analytics, and franchise performance, enabling scale across thousands of locations.',
    highlights: ['Payment processing', 'Real-time analytics', 'Franchise management']
  }
];


const partnerships = [
{ icon: Store, title: 'Franchise a Store', desc: 'For entrepreneurs', id: 'franchise' },
{ icon: Globe, title: 'Country Partner', desc: 'For market leaders', id: 'country' },
{ icon: Settings, title: 'License Tech', desc: 'For institutions', id: 'license' }];

const slideshowImages = [
  {
    url: 'https://images.unsplash.com/photo-1543180930-261980d4868a?w=800&q=80',
    caption: 'Millions walk miles daily for water that may not even be safe to drink.'
  },
  {
    url: 'https://images.unsplash.com/photo-1565879422384-053cc91c8b8b?w=800&q=80',
    caption: 'Single-use plastic bottles pollute Africa\'s landscapes and waterways.'
  },
  {
    url: 'https://images.unsplash.com/photo-1740741705746-74e6f9b93b4f?w=800&q=80',
    caption: 'Access to clean, affordable water should be a right — not a luxury.'
  }
];

function ProblemSlideshow() {
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrent((prev) => (prev + 1) % slideshowImages.length);
    }, 4000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="relative rounded-3xl overflow-hidden aspect-[4/3] shadow-2xl" data-testid="problem-slideshow">
      <AnimatePresence mode="wait">
        <motion.div
          key={current}
          initial={{ opacity: 0, scale: 1.05 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.8 }}
          className="absolute inset-0"
        >
          <img
            src={slideshowImages[current].url}
            alt={slideshowImages[current].caption}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
        </motion.div>
      </AnimatePresence>
      
      {/* Caption */}
      <div className="absolute bottom-0 left-0 right-0 p-6 z-10">
        <AnimatePresence mode="wait">
          <motion.p
            key={current}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.5 }}
            className="text-white text-base font-medium"
          >
            {slideshowImages[current].caption}
          </motion.p>
        </AnimatePresence>
      </div>

      {/* Dots */}
      <div className="absolute bottom-6 right-6 flex gap-2 z-10">
        {slideshowImages.map((_, i) => (
          <button
            key={i}
            onClick={() => setCurrent(i)}
            className={`w-2.5 h-2.5 rounded-full transition-all ${i === current ? 'bg-white w-6' : 'bg-white/50'}`}
            data-testid={`slide-dot-${i}`}
          />
        ))}
      </div>
    </div>
  );
}

function SolutionArcCarousel() {
  const allImages = [...storeImages, ...storeImages];
  const count = allImages.length;
  const radius = 420;

  return (
    <div className="relative w-full flex justify-center overflow-hidden" style={{ height: '520px' }} data-testid="solution-carousel">
      <div
        className="absolute"
        style={{
          width: `${radius * 2 + 240}px`,
          height: `${radius * 2 + 240}px`,
          top: '140px',
          left: '50%',
          transform: 'translateX(-50%)',
        }}
      >
        <div
          className="absolute inset-0"
          style={{ animation: 'arc-spin 50s linear infinite' }}
        >
          {allImages.map((img, i) => {
            const angle = (360 / count) * i;
            const rad = ((angle - 90) * Math.PI) / 180;
            const x = radius * Math.cos(rad);
            const y = radius * Math.sin(rad);

            return (
              <div
                key={i}
                className="absolute shadow-xl"
                style={{
                  left: '50%',
                  top: '50%',
                  width: '190px',
                  height: '240px',
                  borderRadius: '20px',
                  overflow: 'hidden',
                  transform: `translate(-50%, -50%) translate(${x}px, ${y}px) rotate(${angle}deg)`,
                }}
              >
                <img
                  src={img}
                  alt="Ecowater Store"
                  className="w-full h-full object-cover"
                />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function CountrySlideshow() {
  const [active, setActive] = useState(0);
  const countries = [
    {
      name: 'Tanzania',
      url: 'https://ecowater.co.tz',
      iso: 'tz',
      flagUrl: 'https://flagcdn.com/w640/tz.png',
      path: 'M19.8,5.0 L17.2,6.0 L20.1,10.8 L19.6,15.7 L17.5,16.8 L17.5,16.8 L17.8,20.1 L19.4,22.0 L19.5,24.7 L17.7,26.5 L14.7,30.9 L12.0,33.9 L11.2,34.0 L10.8,37.6 L12.2,38.8 L11.9,42.4 L13.3,45.7 L11.5,49.0 L17.4,54.7 L17.9,59.8 L21.5,68.5 L21.5,68.5 L21.9,68.7 L24.8,70.1 L29.4,71.6 L33.6,74.0 L40.8,75.6 L42.2,77.9 L42.2,77.9 L42.7,76.3 L46.4,80.8 L46.8,89.6 L49.2,92.8 L49.1,92.9 L52.0,92.6 L60.7,95.0 L62.7,93.9 L67.8,93.7 L70.6,91.2 L75.0,91.3 L83.1,88.1 L89.2,83.2 L89.2,83.2 L86.5,81.3 L83.7,73.1 L81.2,67.8 L81.9,63.8 L81.5,61.2 L83.6,56.1 L83.4,53.9 L78.8,50.9 L78.4,46.2 L82.0,35.8 L71.5,27.4 L71.0,22.6 L44.3,5.4 L44.3,5.4 L40.6,9.1 L38.1,13.0 L41.0,15.8 L36.8,17.9 L35.9,16.9 L31.6,17.4 L28.2,19.3 L26.2,16.1 L27.6,10.2 L27.9,5.1 L27.9,5.1 L27.9,5.1 L19.8,5.0 Z',
    },
    {
      name: 'Uganda',
      url: 'https://ecowateruganda.co.ug',
      iso: 'ug',
      flagUrl: 'https://flagcdn.com/w640/ug.png',
      path: 'M73.3,5.0 L64.8,12.3 L55.0,12.2 L43.8,16.0 L34.9,12.4 L29.2,16.8 L29.2,16.8 L28.5,35.2 L34.1,37.3 L29.7,42.9 L24.3,47.1 L19.0,55.4 L16.1,62.7 L15.4,75.4 L12.1,81.4 L12.0,93.4 L15.3,95.0 L23.7,90.1 L28.6,88.1 L43.9,88.4 L43.9,88.4 L43.1,82.3 L49.7,73.1 L58.5,70.8 L64.4,67.0 L71.6,70.1 L72.3,71.2 L72.3,70.4 L76.3,64.0 L83.0,53.5 L88.0,42.0 L81.8,24.0 L80.0,16.0 L73.3,5.0 Z',
    },
    {
      name: 'Malawi',
      url: 'https://ecowatertech.mw',
      iso: 'mw',
      flagUrl: 'https://flagcdn.com/w640/mw.png',
      path: 'M53.6,62.3 L53.9,57.8 L51.6,54.3 L51.9,49.1 L49.1,40.4 L52.3,33.8 L52.1,19.4 L48.6,11.8 L48.9,10.5 L48.9,10.5 L47.0,7.2 L36.7,5.0 L41.5,10.3 L43.9,20.4 L42.1,23.6 L39.7,33.3 L41.5,43.1 L38.0,47.3 L34.5,58.2 L39.8,61.3 L45.7,67.0 L48.5,66.0 L52.5,69.0 L53.0,73.7 L50.5,79.3 L50.9,87.6 L57.3,95.0 L60.9,86.8 L65.5,84.2 L65.2,68.9 L61.3,60.3 L57.7,56.5 L57.1,56.5 L57.2,58.2 L59.1,58.6 L61.0,64.9 L60.7,66.4 L57.2,61.9 L55.3,64.8 L53.6,62.3 Z',
    },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setActive((prev) => (prev + 1) % countries.length);
    }, 3500);
    return () => clearInterval(interval);
  }, []);

  const c = countries[active];

  return (
    <div className="w-full h-full flex flex-col items-center justify-center relative">
      <AnimatePresence mode="wait">
        <motion.div
          key={active}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          transition={{ duration: 0.5 }}
          className="flex flex-col items-center cursor-default group"
          data-testid={`slide-${c.name.toLowerCase()}`}
        >
          <svg viewBox="0 0 100 100" className="w-64 h-64 md:w-80 md:h-80 drop-shadow-xl">
            <defs>
              <clipPath id={`clip-${c.name}`}>
                <path d={c.path} />
              </clipPath>
            </defs>
            <image
              href={c.flagUrl}
              x="0" y="0" width="100" height="100"
              preserveAspectRatio="xMidYMid slice"
              clipPath={`url(#clip-${c.name})`}
            />
            <path d={c.path} fill="none" stroke="white" strokeWidth="1.5" opacity="0.6" />
          </svg>
          <div className="mt-6 text-center">
            <p className="text-3xl md:text-4xl font-bold text-slate-900">
              <span className="mr-2"><FlagImg iso={c.iso} size={28} /></span>{c.name}
            </p>
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Dot indicators */}
      <div className="flex gap-2 mt-8">
        {countries.map((_, i) => (
          <button
            key={i}
            onClick={() => setActive(i)}
            className={`w-2.5 h-2.5 rounded-full transition-all ${
              i === active ? 'bg-[#0779BC] w-6' : 'bg-slate-300 hover:bg-slate-400'
            }`}
            data-testid={`slide-dot-${i}`}
          />
        ))}
      </div>
    </div>
  );
}

const COUNTRIES = [
  { name: "Tanzania", code: "+255", flag: "🇹🇿", iso: "tz" },
  { name: "Kenya", code: "+254", flag: "🇰🇪", iso: "ke" },
  { name: "Uganda", code: "+256", flag: "🇺🇬", iso: "ug" },
  { name: "Malawi", code: "+265", flag: "🇲🇼", iso: "mw" },
  { name: "Afghanistan", code: "+93", flag: "🇦🇫", iso: "af" },
  { name: "Albania", code: "+355", flag: "🇦🇱", iso: "al" },
  { name: "Algeria", code: "+213", flag: "🇩🇿", iso: "dz" },
  { name: "Angola", code: "+244", flag: "🇦🇴", iso: "ao" },
  { name: "Argentina", code: "+54", flag: "🇦🇷", iso: "ar" },
  { name: "Australia", code: "+61", flag: "🇦🇺", iso: "au" },
  { name: "Austria", code: "+43", flag: "🇦🇹", iso: "at" },
  { name: "Bangladesh", code: "+880", flag: "🇧🇩", iso: "bd" },
  { name: "Belgium", code: "+32", flag: "🇧🇪", iso: "be" },
  { name: "Benin", code: "+229", flag: "🇧🇯", iso: "bj" },
  { name: "Botswana", code: "+267", flag: "🇧🇼", iso: "bw" },
  { name: "Brazil", code: "+55", flag: "🇧🇷", iso: "br" },
  { name: "Burkina Faso", code: "+226", flag: "🇧🇫", iso: "bf" },
  { name: "Burundi", code: "+257", flag: "🇧🇮", iso: "bi" },
  { name: "Cambodia", code: "+855", flag: "🇰🇭", iso: "kh" },
  { name: "Cameroon", code: "+237", flag: "🇨🇲", iso: "cm" },
  { name: "Canada", code: "+1", flag: "🇨🇦", iso: "ca" },
  { name: "Chad", code: "+235", flag: "🇹🇩", iso: "td" },
  { name: "Chile", code: "+56", flag: "🇨🇱", iso: "cl" },
  { name: "China", code: "+86", flag: "🇨🇳", iso: "cn" },
  { name: "Colombia", code: "+57", flag: "🇨🇴", iso: "co" },
  { name: "Comoros", code: "+269", flag: "🇰🇲", iso: "km" },
  { name: "Congo", code: "+242", flag: "🇨🇬", iso: "cg" },
  { name: "Costa Rica", code: "+506", flag: "🇨🇷", iso: "cr" },
  { name: "Côte d'Ivoire", code: "+225", flag: "🇨🇮", iso: "ci" },
  { name: "Croatia", code: "+385", flag: "🇭🇷", iso: "hr" },
  { name: "Cuba", code: "+53", flag: "🇨🇺", iso: "cu" },
  { name: "Czech Republic", code: "+420", flag: "🇨🇿", iso: "cz" },
  { name: "DRC", code: "+243", flag: "🇨🇩", iso: "cd" },
  { name: "Denmark", code: "+45", flag: "🇩🇰", iso: "dk" },
  { name: "Djibouti", code: "+253", flag: "🇩🇯", iso: "dj" },
  { name: "Ecuador", code: "+593", flag: "🇪🇨", iso: "ec" },
  { name: "Egypt", code: "+20", flag: "🇪🇬", iso: "eg" },
  { name: "Eritrea", code: "+291", flag: "🇪🇷", iso: "er" },
  { name: "Eswatini", code: "+268", flag: "🇸🇿", iso: "sz" },
  { name: "Ethiopia", code: "+251", flag: "🇪🇹", iso: "et" },
  { name: "Finland", code: "+358", flag: "🇫🇮", iso: "fi" },
  { name: "France", code: "+33", flag: "🇫🇷", iso: "fr" },
  { name: "Gabon", code: "+241", flag: "🇬🇦", iso: "ga" },
  { name: "Gambia", code: "+220", flag: "🇬🇲", iso: "gm" },
  { name: "Germany", code: "+49", flag: "🇩🇪", iso: "de" },
  { name: "Ghana", code: "+233", flag: "🇬🇭", iso: "gh" },
  { name: "Greece", code: "+30", flag: "🇬🇷", iso: "gr" },
  { name: "Guinea", code: "+224", flag: "🇬🇳", iso: "gn" },
  { name: "Haiti", code: "+509", flag: "🇭🇹", iso: "ht" },
  { name: "Honduras", code: "+504", flag: "🇭🇳", iso: "hn" },
  { name: "India", code: "+91", flag: "🇮🇳", iso: "in" },
  { name: "Indonesia", code: "+62", flag: "🇮🇩", iso: "id" },
  { name: "Iraq", code: "+964", flag: "🇮🇶", iso: "iq" },
  { name: "Ireland", code: "+353", flag: "🇮🇪", iso: "ie" },
  { name: "Israel", code: "+972", flag: "🇮🇱", iso: "il" },
  { name: "Italy", code: "+39", flag: "🇮🇹", iso: "it" },
  { name: "Jamaica", code: "+1876", flag: "🇯🇲", iso: "jm" },
  { name: "Japan", code: "+81", flag: "🇯🇵", iso: "jp" },
  { name: "Jordan", code: "+962", flag: "🇯🇴", iso: "jo" },
  { name: "Kuwait", code: "+965", flag: "🇰🇼", iso: "kw" },
  { name: "Lebanon", code: "+961", flag: "🇱🇧", iso: "lb" },
  { name: "Lesotho", code: "+266", flag: "🇱🇸", iso: "ls" },
  { name: "Liberia", code: "+231", flag: "🇱🇷", iso: "lr" },
  { name: "Libya", code: "+218", flag: "🇱🇾", iso: "ly" },
  { name: "Madagascar", code: "+261", flag: "🇲🇬", iso: "mg" },
  { name: "Malaysia", code: "+60", flag: "🇲🇾", iso: "my" },
  { name: "Mali", code: "+223", flag: "🇲🇱", iso: "ml" },
  { name: "Mauritania", code: "+222", flag: "🇲🇷", iso: "mr" },
  { name: "Mauritius", code: "+230", flag: "🇲🇺", iso: "mu" },
  { name: "Mexico", code: "+52", flag: "🇲🇽", iso: "mx" },
  { name: "Morocco", code: "+212", flag: "🇲🇦", iso: "ma" },
  { name: "Mozambique", code: "+258", flag: "🇲🇿", iso: "mz" },
  { name: "Myanmar", code: "+95", flag: "🇲🇲", iso: "mm" },
  { name: "Namibia", code: "+264", flag: "🇳🇦", iso: "na" },
  { name: "Nepal", code: "+977", flag: "🇳🇵", iso: "np" },
  { name: "Netherlands", code: "+31", flag: "🇳🇱", iso: "nl" },
  { name: "New Zealand", code: "+64", flag: "🇳🇿", iso: "nz" },
  { name: "Niger", code: "+227", flag: "🇳🇪", iso: "ne" },
  { name: "Nigeria", code: "+234", flag: "🇳🇬", iso: "ng" },
  { name: "Norway", code: "+47", flag: "🇳🇴", iso: "no" },
  { name: "Oman", code: "+968", flag: "🇴🇲", iso: "om" },
  { name: "Pakistan", code: "+92", flag: "🇵🇰", iso: "pk" },
  { name: "Peru", code: "+51", flag: "🇵🇪", iso: "pe" },
  { name: "Philippines", code: "+63", flag: "🇵🇭", iso: "ph" },
  { name: "Poland", code: "+48", flag: "🇵🇱", iso: "pl" },
  { name: "Portugal", code: "+351", flag: "🇵🇹", iso: "pt" },
  { name: "Qatar", code: "+974", flag: "🇶🇦", iso: "qa" },
  { name: "Romania", code: "+40", flag: "🇷🇴", iso: "ro" },
  { name: "Russia", code: "+7", flag: "🇷🇺", iso: "ru" },
  { name: "Rwanda", code: "+250", flag: "🇷🇼", iso: "rw" },
  { name: "Saudi Arabia", code: "+966", flag: "🇸🇦", iso: "sa" },
  { name: "Senegal", code: "+221", flag: "🇸🇳", iso: "sn" },
  { name: "Sierra Leone", code: "+232", flag: "🇸🇱", iso: "sl" },
  { name: "Singapore", code: "+65", flag: "🇸🇬", iso: "sg" },
  { name: "Somalia", code: "+252", flag: "🇸🇴", iso: "so" },
  { name: "South Africa", code: "+27", flag: "🇿🇦", iso: "za" },
  { name: "South Korea", code: "+82", flag: "🇰🇷", iso: "kr" },
  { name: "South Sudan", code: "+211", flag: "🇸🇸", iso: "ss" },
  { name: "Spain", code: "+34", flag: "🇪🇸", iso: "es" },
  { name: "Sri Lanka", code: "+94", flag: "🇱🇰", iso: "lk" },
  { name: "Sudan", code: "+249", flag: "🇸🇩", iso: "sd" },
  { name: "Sweden", code: "+46", flag: "🇸🇪", iso: "se" },
  { name: "Switzerland", code: "+41", flag: "🇨🇭", iso: "ch" },
  { name: "Syria", code: "+963", flag: "🇸🇾", iso: "sy" },
  { name: "Thailand", code: "+66", flag: "🇹🇭", iso: "th" },
  { name: "Togo", code: "+228", flag: "🇹🇬", iso: "tg" },
  { name: "Tunisia", code: "+216", flag: "🇹🇳", iso: "tn" },
  { name: "Turkey", code: "+90", flag: "🇹🇷", iso: "tr" },
  { name: "Ukraine", code: "+380", flag: "🇺🇦", iso: "ua" },
  { name: "United Arab Emirates", code: "+971", flag: "🇦🇪", iso: "ae" },
  { name: "United Kingdom", code: "+44", flag: "🇬🇧", iso: "gb" },
  { name: "United States", code: "+1", flag: "🇺🇸", iso: "us" },
  { name: "Uruguay", code: "+598", flag: "🇺🇾", iso: "uy" },
  { name: "Venezuela", code: "+58", flag: "🇻🇪", iso: "ve" },
  { name: "Vietnam", code: "+84", flag: "🇻🇳", iso: "vn" },
  { name: "Yemen", code: "+967", flag: "🇾🇪", iso: "ye" },
  { name: "Zambia", code: "+260", flag: "🇿🇲", iso: "zm" },
  { name: "Zimbabwe", code: "+263", flag: "🇿🇼", iso: "zw" },
];

const FlagImg = ({ iso, size = 20, className = "" }) => (
  <img
    src={`https://flagcdn.com/w40/${iso}.png`}
    srcSet={`https://flagcdn.com/w80/${iso}.png 2x`}
    alt=""
    width={size}
    height={Math.round(size * 0.75)}
    className={`inline-block rounded-sm object-cover ${className}`}
    loading="lazy"
  />
);

function CountrySelect({ value, onChange, label, showCode, placeholder }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);
  const searchRef = useRef(null);

  const selected = COUNTRIES.find(c => c.name === value);
  const filtered = search
    ? COUNTRIES.filter(c => c.name.toLowerCase().includes(search.toLowerCase()) || c.code.includes(search))
    : COUNTRIES;

  useEffect(() => {
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => {
    if (open && searchRef.current) searchRef.current.focus();
  }, [open]);

  return (
    <div ref={ref} className="relative">
      {label && <Label className="text-slate-700 mb-1.5 block text-sm">{label}</Label>}
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full h-10 px-3 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-900 flex items-center gap-2 focus:outline-none focus:ring-2 focus:ring-[#0779BC] focus:border-transparent text-left"
        data-testid={showCode ? "input-phone-country" : "input-country"}
      >
        {selected ? (
          <>
            <FlagImg iso={selected.iso} size={20} />
            <span className="truncate flex-1">{showCode ? `${selected.code}` : selected.name}</span>
          </>
        ) : (
          <span className="text-slate-400 flex-1">{placeholder}</span>
        )}
        <svg className="w-4 h-4 text-slate-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden">
          <div className="p-2 border-b border-slate-100">
            <input
              ref={searchRef}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full px-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#0779BC]"
              placeholder="Search country..."
              data-testid="country-search"
            />
          </div>
          <div className="max-h-48 overflow-y-auto">
            {filtered.map(c => (
              <button
                key={c.name}
                type="button"
                onClick={() => { onChange(c.name); setOpen(false); setSearch(''); }}
                className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-slate-50 transition-colors text-left ${value === c.name ? 'bg-[#0779BC]/5 text-[#0779BC] font-medium' : 'text-slate-700'}`}
              >
                <FlagImg iso={c.iso} size={18} />
                <span className="flex-1">{c.name}</span>
                {showCode && <span className="text-slate-400 text-xs">{c.code}</span>}
              </button>
            ))}
            {filtered.length === 0 && <p className="px-3 py-4 text-sm text-slate-400 text-center">No results</p>}
          </div>
        </div>
      )}
    </div>
  );
}

function ImpactCounter() {
  const [baseMetrics, setBaseMetrics] = useState(null);
  const [displayed, setDisplayed] = useState({
    connected_campuses: 0,
    connected_users: 0,
    plastic_bottles_avoided: 0,
    co2_prevented_kg: 0,
    money_saved_usd: 0,
    litres_dispensed: 0,
  });
  const baseTimeRef = useRef(Date.now());

  // Per-second tick rates for real-time counting
  const tickRates = {
    connected_campuses: 0,
    connected_users: 0,
    plastic_bottles_avoided: 0,
    co2_prevented_kg: 0,
    money_saved_usd: 0,
    litres_dispensed: 0,
  };

  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        // Try PHP proxy first (cPanel), fallback to backend API
        let res = await fetch(`${BACKEND_URL}/impact-proxy.php`).catch(() => null);
        if (!res || !res.ok) {
          res = await fetch(`${BACKEND_URL}/api/impact`).catch(() => null);
        }
        if (res && res.ok) {
          const data = await res.json();
          setBaseMetrics(data);
          baseTimeRef.current = Date.now();
        }
      } catch (e) { /* silent */ }
    };
    fetchMetrics();
    const interval = setInterval(fetchMetrics, 60000);
    return () => clearInterval(interval);
  }, []);

  // Real-time tick animation
  useEffect(() => {
    if (!baseMetrics) return;
    let raf;
    const tick = () => {
      const elapsed = (Date.now() - baseTimeRef.current) / 1000;
      const next = {};
      for (const key of Object.keys(baseMetrics)) {
        next[key] = Math.round(baseMetrics[key] + elapsed * (tickRates[key] || 0));
      }
      setDisplayed(next);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [baseMetrics]);

  const formatNum = (n) => {
    return n.toLocaleString();
  };

  const items = [
    { key: 'connected_campuses', label: 'Connected Campuses', icon: Building2, color: '#7CB342' },
    { key: 'connected_users', label: 'Connected Users', icon: Users, color: '#8B5CF6' },
    { key: 'plastic_bottles_avoided', label: 'Plastics Avoided', icon: Recycle, color: '#0779BC' },
    { key: 'co2_prevented_kg', label: 'CO\u2082 Prevented (kg)', icon: Wind, color: '#26A69A' },
    { key: 'money_saved_usd', label: 'Money Saved by Users', icon: TrendingUp, color: '#F59E0B' },
    { key: 'litres_dispensed', label: 'Litres Dispensed', icon: Droplets, color: '#0779BC' },
  ];

  return (
    <div className="grid grid-cols-2 gap-4">
      {items.map((item, i) => {
        const Icon = item.icon;
        return (
          <motion.div
            key={item.key}
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.1 }}
            className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 flex items-center gap-4 hover:shadow-md transition-shadow"
            data-testid={`impact-${item.key}`}
          >
            <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: item.color + '15' }}>
              <Icon size={20} style={{ color: item.color }} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs text-slate-400 font-medium uppercase tracking-wider">{item.label}</p>
              <p className="text-xl md:text-2xl font-bold text-slate-900 tabular-nums">
                {item.key === 'money_saved_usd' && '$'}{formatNum(displayed[item.key])}{item.key === 'connected_campuses' && '+'}
              </p>
            </div>
            <div className="w-2 h-2 rounded-full animate-pulse shrink-0" style={{ backgroundColor: item.color }} />
          </motion.div>
        );
      })}
    </div>
  );
}

function App() {
  const [activeSection, setActiveSection] = useState('home');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const sectionRefs = useRef({});

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTech, setActiveTech] = useState(0);
  const techTimerRef = useRef(null);

  // Auto-advance How It Works steps
  const startTechTimer = () => {
    if (techTimerRef.current) clearInterval(techTimerRef.current);
    techTimerRef.current = setInterval(() => {
      setActiveTech((prev) => (prev + 1) % techSteps.length);
    }, 6000);
  };

  useEffect(() => {
    startTechTimer();
    return () => clearInterval(techTimerRef.current);
  }, []);

  const handleTechClick = (i) => {
    setActiveTech(i);
    startTechTimer();
  };
  const [marketTab, setMarketTab] = useState('operating');
  const [showContactForm, setShowContactForm] = useState(false);
  const [contactFormContext, setContactFormContext] = useState('');
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', phoneCountry: 'Tanzania', country: 'Tanzania', message: ''
  });

  // Typing animation state
  const [currentPhraseIndex, setCurrentPhraseIndex] = useState(0);
  const [displayText, setDisplayText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  // Typing animation effect
  useEffect(() => {
    const currentPhrase = typingPhrases[currentPhraseIndex];
    const typingSpeed = isDeleting ? 50 : 100;
    const pauseTime = 2000;

    const timeout = setTimeout(() => {
      if (!isDeleting) {
        if (displayText.length < currentPhrase.length) {
          setDisplayText(currentPhrase.slice(0, displayText.length + 1));
        } else {
          setTimeout(() => setIsDeleting(true), pauseTime);
        }
      } else {
        if (displayText.length > 0) {
          setDisplayText(displayText.slice(0, -1));
        } else {
          setIsDeleting(false);
          setCurrentPhraseIndex((prev) => (prev + 1) % typingPhrases.length);
        }
      }
    }, typingSpeed);

    return () => clearTimeout(timeout);
  }, [displayText, isDeleting, currentPhraseIndex]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
      const sections = ['home', 'story', 'technology', 'join', 'markets', 'contact'];
      const scrollPosition = window.scrollY + 100;
      for (const section of sections) {
        const element = sectionRefs.current[section];
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section);
            break;
          }
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    setMobileMenuOpen(false);
    const element = sectionRefs.current[id];
    if (element) {
      const top = element.offsetTop - 80;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    const phoneCode = COUNTRIES.find(c => c.name === formData.phoneCountry)?.code || '';
    const fullPhone = phoneCode + ' ' + formData.phone;
    const payload = { name: formData.name, email: formData.email, phone: fullPhone, country: formData.country, message: formData.message, interest: contactFormContext };
    try {
      // Try PHP endpoint first (cPanel), fallback to backend API
      let response = await fetch(`${BACKEND_URL}/contact.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      }).catch(() => null);

      if (!response || !response.ok) {
        response = await fetch(`${BACKEND_URL}/api/inquiries`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...payload, type: 'partnership' })
        });
      }

      if (response.ok) {
        toast.success('Message sent!');
        setFormData({ name: '', email: '', phone: '', phoneCountry: 'Tanzania', country: 'Tanzania', message: '' });
        setShowContactForm(false);
      } else {
        toast.error('Failed to send. Try again.');
      }
    } catch (error) {
      toast.error('Failed to send. Try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="App font-sans">
      <BrowserRouter>
        {/* HEADER */}
        <header className="fixed top-0 left-0 right-0 z-50 bg-slate-900">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="flex items-center justify-between h-20">
              <button onClick={() => scrollToSection('home')} className="flex items-center">
                <img src={LOGO_URL} alt="Ecowater" className="h-7 brightness-0 invert" />
              </button>
              
              <nav className="hidden lg:flex items-center gap-8">
                {navLinks.map((link) =>
                <button
                  key={link.id}
                  onClick={() => scrollToSection(link.id)}
                  className={`text-sm font-medium transition-colors ${activeSection === link.id ? 'text-white' : 'text-white/80 hover:text-white'}`}>

                    {link.name}
                  </button>
                )}
              </nav>

              <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="lg:hidden text-white p-2">
                {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen &&
          <div className="lg:hidden bg-slate-900 border-t border-white/10">
              <nav className="flex flex-col p-4">
                {navLinks.map((link) =>
              <button key={link.id} onClick={() => scrollToSection(link.id)} className="py-3 text-left text-white/90 hover:text-white font-medium">
                    {link.name}
                  </button>
              )}
              </nav>
            </div>
          }
        </header>

        <main>
          {/* HERO */}
          <section ref={(el) => sectionRefs.current['home'] = el} id="home" className="relative" data-testid="home-section">
            {/* Background Image with Teal Overlay and Rounded Bottom */}
            <div className="relative min-h-screen rounded-b-[3rem] overflow-hidden">
              <div className="absolute inset-0">
                <img src={HERO_BG} alt="" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-black/30" />
              </div>

              <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 pt-32 pb-56">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8 }}
                  className="max-w-3xl mt-16 md:mt-24">

                  <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight">
                    Clean water<br />
                    that simply<br />
                    <span className="text-white/90">works.</span>
                  </h1>
                  <p className="mt-8 text-xl text-white/90 max-w-xl">
                    Unlimited refills. Anytime. Anywhere in the network.
                  </p>
                </motion.div>
              </div>
            </div>

            {/* Slate background extension behind the card */}
            <div className="absolute bottom-0 left-0 right-0 h-32 bg-slate-50" style={{ transform: 'translateY(100%)' }} />

            {/* Overlapping White Card */}
            <div className="relative -mt-32 px-6 lg:px-12 pb-16 z-10">
              <motion.div 
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.3 }}
                className="max-w-4xl mx-auto bg-white rounded-3xl shadow-2xl p-8 md:p-12 text-center relative overflow-hidden"
              >
                {/* Decorative background elements */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-[#0779BC]/5 rounded-full -translate-y-1/2 translate-x-1/2" />
                <div className="absolute bottom-0 left-0 w-24 h-24 bg-[#0779BC]/5 rounded-full translate-y-1/2 -translate-x-1/2" />

                {/* Typing title */}
                <div className="relative z-10">
                  <div className="h-16 md:h-20 flex items-center justify-center mb-4">
                    <h2 className="text-3xl md:text-4xl font-bold text-slate-900">
                      {displayText}
                      <motion.span 
                        className="inline-block w-1 h-8 md:h-10 bg-slate-900 ml-1 align-middle"
                        animate={{ opacity: [1, 0] }}
                        transition={{ duration: 0.5, repeat: Infinity, repeatType: "reverse" }}
                      />
                    </h2>
                  </div>
                  
                  <p className="text-lg md:text-xl text-slate-600 max-w-2xl mx-auto mb-4 leading-relaxed">
                    Ecowater is building Africa's first continental clean-water network, delivering safe, affordable, and plastic-free drinking water through smart refill systems.
                  </p>
                  <p className="text-lg md:text-xl text-slate-600 max-w-2xl mx-auto mb-8 leading-relaxed">
                    From university campuses and schools to communities, Ecowater combines technology, sustainability, and entrepreneurship to make clean water part of everyday life.
                  </p>
                  
                  <Button
                    onClick={() => scrollToSection('join')}
                    className="bg-[#0779BC] hover:bg-[#06649b] text-white rounded-full px-10 py-4 text-lg font-medium group"
                    data-testid="hero-join-btn"
                  >
                    JoinTheNetwork
                    <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
                  </Button>
                </div>
              </motion.div>
            </div>
          </section>

          {/* Slate background spacer */}
          <div className="h-16 bg-slate-50" />

          {/* OUR STORY SECTION */}
          <section ref={(el) => sectionRefs.current['story'] = el} id="story" className="py-24 bg-slate-50 overflow-hidden" data-testid="story-section">
            <div className="relative">
              <div className="max-w-7xl mx-auto px-6 lg:px-12">
                <div className="grid lg:grid-cols-2 gap-8 items-center">
                  
                  {/* Large Half Circle Image Container */}
                  <motion.div
                    initial={{ opacity: 0, x: -100 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 1, ease: "easeOut" }}
                    className="relative -ml-6 lg:-ml-32">

                    {/* Half circle container */}
                    <div className="relative">
                      <div
                        className="relative w-[500px] h-[500px] lg:w-[600px] lg:h-[600px] overflow-hidden"
                        style={{
                          borderRadius: '0 50% 50% 0'
                        }}>

                        <img
                          src={TEAM_PHOTO}
                          alt="Ecowater Team"
                          className="w-full h-full object-cover" />

                        {/* Overlay gradient */}
                        <div className="absolute inset-0 bg-gradient-to-r from-[#0779BC]/20 to-transparent" />
                      </div>
                      
                      {/* Decorative border */}
                      <div
                        className="absolute -inset-3 border-2 border-[#0779BC]/30 pointer-events-none"
                        style={{ borderRadius: '0 50% 50% 0' }} />

                      
                      {/* Animated floating icons */}
                      <motion.div
                        className="absolute top-12 right-8 w-14 h-14 bg-white rounded-2xl shadow-lg flex items-center justify-center"
                        animate={{ y: [0, -10, 0] }}
                        transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}>

                        <Droplets className="text-[#0779BC]" size={28} />
                      </motion.div>
                      
                      <motion.div
                        className="absolute bottom-24 right-4 w-12 h-12 bg-[#0779BC] rounded-xl shadow-lg flex items-center justify-center"
                        animate={{ y: [0, 10, 0] }}
                        transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}>

                        <Leaf className="text-white" size={24} />
                      </motion.div>
                      
                      <motion.div
                        className="absolute top-1/2 right-0 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center"
                        animate={{ scale: [1, 1.1, 1] }}
                        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut", delay: 1 }}>

                        <Wifi className="text-[#0779BC]" size={20} />
                      </motion.div>
                    </div>
                    
                    {/* Background decorative circles */}
                    <div className="absolute -top-10 -left-10 w-32 h-32 bg-[#0779BC]/5 rounded-full" />
                    <div className="absolute -bottom-6 left-20 w-20 h-20 bg-[#0779BC]/10 rounded-full" />
                  </motion.div>

                  {/* Content */}
                  <motion.div
                    initial={{ opacity: 0, x: 50 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, delay: 0.3 }}
                    className="lg:pl-8">

                    <motion.div
                      initial={{ width: 0 }}
                      whileInView={{ width: 60 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.6, delay: 0.5 }}
                      className="h-1 bg-[#0779BC] mb-6" />

                    
                    <h2 className="text-4xl md:text-5xl font-bold text-[#0779BC] mb-8">
                      Our Story
                    </h2>
                    
                    <p className="text-lg text-slate-700 mb-6 leading-relaxed">From a single campus in Tanzania, Ecowater has grown into one of Africa's most recognized water innovations serving hundreds of thousands of users and proving that Africa can lead in sustainable hydration.

                    </p>
                    
                    <p className="text-lg text-slate-700 mb-8 leading-relaxed">
                      Today, Ecowater is evolving into a continental platform connecting water, technology, and opportunity.
                    </p>

                    {/* Systems icons */}
                    <div className="grid grid-cols-2 gap-4 mb-8">
                      <motion.div
                        className="flex items-center gap-3 p-3 bg-white rounded-xl shadow-sm"
                        whileHover={{ scale: 1.02, boxShadow: "0 10px 30px rgba(7,121,188,0.1)" }}>

                        <div className="w-10 h-10 rounded-lg bg-[#0779BC]/10 flex items-center justify-center">
                          <Droplets className="text-[#0779BC]" size={20} />
                        </div>
                        <span className="text-sm font-medium text-slate-700">Smart purification</span>
                      </motion.div>
                      
                      <motion.div
                        className="flex items-center gap-3 p-3 bg-white rounded-xl shadow-sm"
                        whileHover={{ scale: 1.02, boxShadow: "0 10px 30px rgba(7,121,188,0.1)" }}>

                        <div className="w-10 h-10 rounded-lg bg-[#0779BC]/10 flex items-center justify-center">
                          <CreditCard className="text-[#0779BC]" size={20} />
                        </div>
                        <span className="text-sm font-medium text-slate-700">Cashless payments</span>
                      </motion.div>
                      
                      <motion.div
                        className="flex items-center gap-3 p-3 bg-white rounded-xl shadow-sm"
                        whileHover={{ scale: 1.02, boxShadow: "0 10px 30px rgba(7,121,188,0.1)" }}>

                        <div className="w-10 h-10 rounded-lg bg-[#0779BC]/10 flex items-center justify-center">
                          <Leaf className="text-[#0779BC]" size={20} />
                        </div>
                        <span className="text-sm font-medium text-slate-700">Reusable containers</span>
                      </motion.div>
                      
                      <motion.div
                        className="flex items-center gap-3 p-3 bg-white rounded-xl shadow-sm"
                        whileHover={{ scale: 1.02, boxShadow: "0 10px 30px rgba(7,121,188,0.1)" }}>

                        <div className="w-10 h-10 rounded-lg bg-[#0779BC]/10 flex items-center justify-center">
                          <Globe className="text-[#0779BC]" size={20} />
                        </div>
                        <span className="text-sm font-medium text-slate-700">Decentralized Infrastructure</span>
                      </motion.div>
                    </div>
                    
                    <p className="text-slate-600 mb-8">
                      The result is a new model for water access that is affordable, scalable, and environmentally responsible.
                    </p>

                    <Button
                      onClick={() => scrollToSection('technology')}
                      className="bg-[#0779BC] hover:bg-[#06649b] text-white rounded-full px-8 py-3 font-medium group"
                      data-testid="story-learn-more-btn">

                      Learn More
                      <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={18} />
                    </Button>
                  </motion.div>
                </div>
              </div>
            </div>
          </section>

          {/* THE PROBLEM SECTION */}
          <section className="relative text-white overflow-hidden" data-testid="problem-section">
            {/* Seamless SVG wave top */}
            <div className="bg-slate-50">
              <svg viewBox="0 0 1440 100" preserveAspectRatio="none" className="block w-full h-[60px] md:h-[80px]">
                <path d="M0,100 C360,100 360,0 720,0 C1080,0 1080,100 1440,100 L1440,100 L0,100 Z" fill="#0779BC" />
              </svg>
            </div>
            
            <div className="bg-[#0779BC] relative">
              {/* Background pattern */}
              <div className="absolute inset-0 opacity-10">
                <div className="absolute inset-0" style={{
                  backgroundImage: `radial-gradient(circle at 20% 50%, rgba(255,255,255,0.1) 0%, transparent 50%),
                                    radial-gradient(circle at 80% 20%, rgba(255,255,255,0.1) 0%, transparent 40%),
                                    radial-gradient(circle at 40% 80%, rgba(255,255,255,0.05) 0%, transparent 30%)`
                }} />
              </div>

              <div className="relative py-20 md:py-28">
                <div className="max-w-6xl mx-auto px-6 lg:px-12">
                  {/* Header */}
                  <motion.div 
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="text-center mb-10"
                  >
                    <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
                      Africa Is Overpaying<br />for Drinking Water
                    </h2>
                  </motion.div>

                  {/* Text */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                    className="text-center max-w-3xl mx-auto mb-10"
                  >
                    <p className="text-xl text-white/90 mb-4 leading-relaxed">
                      Across Africa, millions of people cannot depend on tap water as a safe source of drinking water. As a result, households and students turn to bottled water as their everyday solution.
                    </p>
                    <p className="text-xl text-white/90 leading-relaxed">
                      But bottled water creates a costly and unsustainable cycle. A typical university student can spend up to <span className="text-white font-bold bg-white/20 px-2 py-1 rounded">$400 per year</span> on bottled water, simply to stay hydrated.
                    </p>
                  </motion.div>

                  {/* "At the same time" label */}
                  <div className="text-center mb-8">
                    <span className="inline-block px-5 py-2 bg-white/10 rounded-full text-white font-medium text-sm">
                      At the same time:
                    </span>
                  </div>

                  {/* 3-column cards */}
                  <div className="grid md:grid-cols-3 gap-6 mb-16 md:mb-20">
                    <motion.div 
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.1 }}
                      className="bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20 p-6 hover:bg-white/15 transition-all"
                      data-testid="problem-stat-0"
                    >
                      <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center shadow-lg mb-4">
                        <TrendingUp className="text-[#0779BC]" size={24} />
                      </div>
                      <p className="text-white/90 leading-relaxed">Bottled water can cost up to <span className="text-white font-bold">1000 times</span> more than tap water.</p>
                    </motion.div>
                    
                    <motion.div 
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.2 }}
                      className="bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20 p-6 hover:bg-white/15 transition-all"
                      data-testid="problem-stat-1"
                    >
                      <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center shadow-lg mb-4">
                        <Leaf className="text-[#0779BC]" size={24} />
                      </div>
                      <p className="text-white/90 leading-relaxed"><span className="text-white font-bold">80%</span> of plastic bottles end up polluting land and water systems.</p>
                    </motion.div>
                    
                    <motion.div 
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.3 }}
                      className="bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20 p-6 hover:bg-white/15 transition-all"
                      data-testid="problem-stat-2"
                    >
                      <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center shadow-lg mb-4">
                        <Flame className="text-[#0779BC]" size={24} />
                      </div>
                      <p className="text-white/90 leading-relaxed">Millions of people still boil water using charcoal or LPG, adding cost and environmental damage.</p>
                    </motion.div>
                  </div>

                  {/* Closing line */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, delay: 0.3 }}
                    className="text-center border-t border-white/20 pt-12 md:pt-16"
                    data-testid="problem-closing-line"
                  >
                    <p className="text-2xl md:text-3xl lg:text-4xl font-light leading-snug tracking-wide">
                      Africa doesn't lack water.
                    </p>
                    <p className="text-2xl md:text-3xl lg:text-4xl font-bold leading-snug mt-2">
                      It lacks a reliable system for drinking water.
                    </p>
                  </motion.div>
                </div>
              </div>
            </div>

            {/* Seamless SVG wave bottom */}
            <div className="bg-white">
              <svg viewBox="0 0 1440 100" preserveAspectRatio="none" className="block w-full h-[60px] md:h-[80px]">
                <path d="M0,0 C360,0 360,100 720,100 C1080,100 1080,0 1440,0 L1440,0 L0,0 Z" fill="#0779BC" />
              </svg>
            </div>
          </section>

          {/* SOLUTION SECTION */}
          <section className="py-24 md:py-32 bg-white" data-testid="solution-section">
            <div className="max-w-6xl mx-auto px-6 lg:px-12">

              {/* Hero heading + store image */}
              <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center mb-20">
                {/* Left — heading + subtitle */}
                <motion.div
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.7 }}
                >
                  <p className="text-lg md:text-xl font-medium italic text-slate-400 mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    It's only
                  </p>
                  <h2 className="text-5xl md:text-6xl lg:text-7xl font-bold text-[#0779BC] leading-none mb-4">
                    Ecowater
                  </h2>
                  <p className="text-2xl md:text-3xl font-bold text-slate-900 leading-snug mb-6">
                    A New Infrastructure<br />for Drinking Water
                  </p>
                  <p className="text-lg text-slate-500 leading-relaxed max-w-md">
                    Decentralized refill stores that purify water at the point of use, replacing single use plastic bottled water with a smart, cashless network.
                  </p>
                </motion.div>

                {/* Right — store image */}
                <motion.div
                  initial={{ opacity: 0, x: 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.7, delay: 0.15 }}
                  className="relative"
                >
                  <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                    <img
                      src="images/solution-store.jpg"
                      alt="Ecowater Mazengo Store"
                      className="w-full h-[400px] object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0779BC]/40 via-transparent to-transparent" />
                  </div>
                  {/* Floating accent */}
                  <div className="absolute -bottom-4 -left-4 w-28 h-28 bg-[#0779BC]/10 rounded-3xl -z-10" />
                  <div className="absolute -top-4 -right-4 w-20 h-20 bg-[#0779BC]/5 rounded-2xl -z-10" />
                </motion.div>
              </div>

              {/* 4 feature cards */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mb-20">
                {[
                  { icon: TrendingUp, stat: '80%', label: 'cheaper than bottled water' },
                  { icon: Recycle, stat: 'Zero', label: 'single-use plastic' },
                  { icon: Smartphone, stat: 'Tap & Pay', label: 'mobile payments' },
                  { icon: Clock, stat: '24/7', label: 'always available' },
                ].map((card, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                    className="relative bg-slate-50 rounded-3xl p-7 border border-slate-100 overflow-hidden group hover:border-[#0779BC]/30 transition-colors"
                    data-testid={`solution-feat-${i}`}
                  >
                    <div className="absolute top-0 right-0 w-24 h-24 bg-[#0779BC]/5 rounded-full -translate-y-1/2 translate-x-1/2 group-hover:bg-[#0779BC]/10 transition-colors" />
                    <div className="relative">
                      <div className="w-11 h-11 rounded-xl bg-[#0779BC] flex items-center justify-center mb-5">
                        <card.icon className="text-white" size={22} />
                      </div>
                      <p className="text-3xl font-bold text-slate-900 mb-1">{card.stat}</p>
                      <p className="text-sm text-slate-500">{card.label}</p>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Impact strip — bottle + blue banner */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="grid lg:grid-cols-5 gap-0 rounded-3xl overflow-hidden shadow-xl"
              >
                {/* Left — Bottle image */}
                <div className="lg:col-span-2 relative bg-slate-100">
                  <img
                    src="images/problem-bottles.jpg"
                    alt="Ecowater reusable bottle"
                    className="w-full h-full object-cover min-h-[320px]"
                  />
                </div>

                {/* Right — Blue banner */}
                <div className="lg:col-span-3 bg-[#0779BC] p-8 md:p-12 flex flex-col justify-center">
                  <p className="text-white/60 text-sm font-medium uppercase tracking-widest mb-6">For Every Refill</p>
                  <div className="space-y-4 mb-8">
                    <div className="flex items-center gap-4 bg-white/10 backdrop-blur-sm rounded-2xl p-5 border border-white/20">
                      <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center flex-shrink-0">
                        <Droplets className="text-[#0779BC]" size={24} />
                      </div>
                      <div>
                        <p className="text-xl font-bold text-white">1 plastic bottle</p>
                        <p className="text-white/70 text-sm">saved from waste</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 bg-white/10 backdrop-blur-sm rounded-2xl p-5 border border-white/20">
                      <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center flex-shrink-0">
                        <Wind className="text-[#0779BC]" size={24} />
                      </div>
                      <div>
                        <p className="text-xl font-bold text-white">165g CO&#8322;</p>
                        <p className="text-white/70 text-sm">emissions avoided</p>
                      </div>
                    </div>
                  </div>
                  <p className="text-white/90 text-lg leading-relaxed">
                    By combining water purification, digital payments, and refill culture, Ecowater transforms water access from a packaged product into a shared infrastructure.
                  </p>
                  <p className="text-white font-semibold text-xl mt-4">
                    Clean water becomes simple, affordable, and sustainable.
                  </p>
                </div>
              </motion.div>
            </div>
          </section>

          {/* TECHNOLOGY SECTION */}
          <section ref={(el) => sectionRefs.current['technology'] = el} id="technology" className="relative py-24 md:py-32 bg-slate-50 overflow-hidden" data-testid="technology-section">
            <div
              className="absolute inset-0 opacity-10"
              style={{
                backgroundImage: 'url(images/how-it-works-bg.jpg)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
            />
            <div className="relative z-10 max-w-6xl mx-auto px-6 lg:px-12">

              {/* Heading */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-center mb-6"
              >
                <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight mb-4">
                  How It <span className="text-[#0779BC]">Works</span>
                </h2>
                <p className="text-lg text-slate-500 max-w-2xl mx-auto">
                  A technology-enabled water platform connecting refill stores, digital payments, and cloud monitoring into a single system.
                </p>
              </motion.div>

              {/* Vertical pipeline diagram + content */}
              <div className="mt-16 grid lg:grid-cols-12 gap-8 lg:gap-12 items-start">

                {/* LEFT — Vertical step diagram */}
                <div className="lg:col-span-4">
                  <div className="flex flex-col items-center lg:items-start">
                    {techSteps.map((step, i) => (
                      <div key={i} className="flex flex-col items-center lg:items-start w-full">
                        {/* Step button */}
                        <button
                          onClick={() => handleTechClick(i)}
                          className={`group w-full flex items-center gap-4 px-5 py-4 rounded-xl transition-all duration-300 ${
                            activeTech === i
                              ? 'bg-[#0779BC] text-white shadow-lg shadow-[#0779BC]/20'
                              : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
                          }`}
                          data-testid={`tech-tab-${i}`}
                        >
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 transition-all ${
                            activeTech === i ? 'bg-white/20' : 'bg-slate-100 group-hover:bg-slate-200'
                          }`}>
                            <step.icon size={20} className={activeTech === i ? 'text-white' : 'text-[#0779BC]'} />
                          </div>
                          <div className="text-left">
                            <span className={`text-xs font-semibold tracking-wider uppercase ${activeTech === i ? 'text-white/60' : 'text-slate-400'}`}>Step {step.num}</span>
                            <p className={`text-sm font-semibold leading-tight ${activeTech === i ? 'text-white' : 'text-slate-700'}`}>{step.tab}</p>
                          </div>
                        </button>

                        {/* Arrow connector */}
                        {i < techSteps.length - 1 && (
                          <div className="flex flex-col items-center py-1.5 lg:pl-9">
                            <div className={`w-0.5 h-5 ${activeTech === i || activeTech === i + 1 ? 'bg-[#0779BC]' : 'bg-slate-200'} transition-colors`} />
                            <svg width="12" height="8" viewBox="0 0 12 8" className={`${activeTech === i || activeTech === i + 1 ? 'text-[#0779BC]' : 'text-slate-300'} transition-colors`}>
                              <path d="M6 8L0 0h12L6 8z" fill="currentColor" />
                            </svg>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* RIGHT — Active content panel */}
                <div className="lg:col-span-8">
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={activeTech}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      transition={{ duration: 0.35 }}
                      className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden"
                      data-testid="tech-content-card"
                    >
                      {/* Blue header area */}
                      <div className="bg-[#0779BC] p-8 md:p-10 relative overflow-hidden">
                        <div className="absolute -top-10 -right-10 w-40 h-40 bg-white/5 rounded-full" />
                        <div className="absolute -bottom-8 -left-8 w-32 h-32 bg-white/5 rounded-full" />
                        <div className="relative flex items-start gap-6">
                          <span className="text-white/20 text-6xl md:text-7xl font-bold leading-none shrink-0">{techSteps[activeTech].num}</span>
                          <div>
                            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mb-3">
                              {(() => { const Icon = techSteps[activeTech].icon; return <Icon className="text-white" size={24} />; })()}
                            </div>
                            <h3 className="text-2xl md:text-3xl font-bold text-white">{techSteps[activeTech].title}</h3>
                            <p className="text-white/70 mt-1">{techSteps[activeTech].short}</p>
                          </div>
                        </div>
                      </div>

                      {/* Details area */}
                      <div className="p-8 md:p-10">
                        <p className="text-slate-600 text-lg leading-relaxed mb-8">
                          {techSteps[activeTech].details}
                        </p>
                        <div className="flex flex-wrap gap-3">
                          {techSteps[activeTech].highlights.map((h, j) => (
                            <motion.span
                              key={h}
                              initial={{ opacity: 0, scale: 0.9 }}
                              animate={{ opacity: 1, scale: 1 }}
                              transition={{ delay: j * 0.1 }}
                              className="px-4 py-2 bg-[#0779BC]/10 text-[#0779BC] rounded-full text-sm font-medium"
                            >
                              {h}
                            </motion.span>
                          ))}
                        </div>
                      </div>
                    </motion.div>
                  </AnimatePresence>
                </div>
              </div>

              {/* "A Network That Gets Smarter" */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="mt-20 text-center"
              >
                <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">A Network That Gets Smarter</h3>
                <p className="text-slate-500 max-w-xl mx-auto mb-10">
                  Each new store strengthens the platform. As more stores connect:
                </p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto">
                  {[
                    { label: 'Access expands', icon: Globe },
                    { label: 'Data improves', icon: TrendingUp },
                    { label: 'Costs decrease', icon: TrendingUp },
                    { label: 'Impact increases', icon: Leaf },
                  ].map((item, i) => (
                    <motion.div
                      key={item.label}
                      initial={{ opacity: 0, y: 12 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.1 + i * 0.08 }}
                      className="bg-white rounded-2xl p-5 border border-slate-100 text-center"
                    >
                      <div className="w-10 h-10 mx-auto mb-3 bg-[#0779BC]/10 rounded-xl flex items-center justify-center">
                        <item.icon className="text-[#0779BC]" size={20} />
                      </div>
                      <p className="text-sm font-medium text-slate-700">{item.label}</p>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </div>
          </section>

          {/* JOIN THE NETWORK SECTION */}
          <section ref={(el) => sectionRefs.current['join'] = el} id="join" className="relative bg-[#0779BC] overflow-hidden" data-testid="join-section">
            {/* Top wave */}
            <div className="bg-slate-50">
              <svg viewBox="0 0 1440 80" preserveAspectRatio="none" className="block w-full h-[50px] md:h-[70px]">
                <path d="M0,80 C480,80 480,0 960,0 C1200,0 1320,40 1440,80 L1440,80 L0,80 Z" fill="#0779BC" />
              </svg>
            </div>

            {/* Background accents */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              <div className="absolute -top-40 -right-40 w-96 h-96 bg-white/5 rounded-full" />
              <div className="absolute top-1/2 -left-32 w-72 h-72 bg-white/5 rounded-full" />
              <div className="absolute bottom-20 right-1/4 w-48 h-48 bg-white/5 rounded-full" />
            </div>

            <div className="relative py-14 md:py-20">
              <div className="max-w-6xl mx-auto px-6 lg:px-12">

                {/* Header */}
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="text-center mb-6"
                >
                  <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-4">
                    #JoinTheEcowaterNetwork
                  </h2>
                  <p className="text-xl md:text-2xl text-white/70 font-medium mb-4">
                    We spent 10 years learning how it's done.<br />So you don't have to.
                  </p>
                </motion.div>

                {/* 3 Partnership cards */}
                <div className="grid lg:grid-cols-3 gap-6 mt-16">
                  {/* Franchise a Store */}
                  <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.1 }}
                    className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 border border-white/20 flex flex-col"
                    data-testid="partnership-franchise"
                  >
                    <div className="w-14 h-14 bg-white rounded-2xl flex items-center justify-center mb-5">
                      <Store className="text-[#0779BC]" size={28} />
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-3">Franchise a Store</h3>
                    <p className="text-white/70 leading-relaxed mb-6 flex-1">
                      Operate an Ecowater refill station in your community. We provide technology, systems, training, and brand.
                    </p>
                    <Button
                      onClick={() => { setContactFormContext('Franchise a Store'); setShowContactForm(true); }}
                      className="w-full bg-white text-[#0779BC] hover:bg-white/90 rounded-full py-3 font-medium"
                      data-testid="cta-franchise"
                    >
                      Start a Store <ArrowRight size={16} className="ml-2 inline" />
                    </Button>
                  </motion.div>

                  {/* Country Partner */}
                  <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.2 }}
                    className="bg-white rounded-3xl p-8 shadow-2xl shadow-black/10 flex flex-col relative overflow-hidden"
                    data-testid="partnership-country"
                  >
                    <div className="absolute top-0 right-0 bg-[#0779BC] text-white text-xs font-bold px-4 py-1.5 rounded-bl-xl">
                      HIGH IMPACT
                    </div>
                    <div className="w-14 h-14 bg-[#0779BC] rounded-2xl flex items-center justify-center mb-5">
                      <Globe className="text-white" size={28} />
                    </div>
                    <h3 className="text-2xl font-bold text-slate-900 mb-3">Bring Ecowater to Your Country</h3>
                    <p className="text-slate-600 leading-relaxed mb-6 flex-1">
                      Lead the national expansion of the Ecowater network. We provide the platform. You build the market.
                    </p>
                    <Button
                      onClick={() => { setContactFormContext('Country Partner'); setShowContactForm(true); }}
                      className="w-full bg-[#0779BC] hover:bg-[#06649b] text-white rounded-full py-3 font-medium"
                      data-testid="cta-country"
                    >
                      Become a Country Partner <ArrowRight size={16} className="ml-2 inline" />
                    </Button>
                  </motion.div>

                  {/* License Tech */}
                  <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.3 }}
                    className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 border border-white/20 flex flex-col"
                    data-testid="partnership-license"
                  >
                    <div className="w-14 h-14 bg-white rounded-2xl flex items-center justify-center mb-5">
                      <Cpu className="text-[#0779BC]" size={28} />
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-3">License Our Technology</h3>
                    <p className="text-white/70 leading-relaxed mb-6 flex-1">
                      Deploy Ecowater purification and payment systems in your institution or infrastructure.
                    </p>
                    <Button
                      onClick={() => { setContactFormContext('License Technology'); setShowContactForm(true); }}
                      className="w-full bg-white text-[#0779BC] hover:bg-white/90 rounded-full py-3 font-medium"
                      data-testid="cta-license"
                    >
                      License Technology <ArrowRight size={16} className="ml-2 inline" />
                    </Button>
                  </motion.div>
                </div>
              </div>
            </div>

            {/* Bottom wave */}
            <div className="bg-slate-50">
              <svg viewBox="0 0 1440 80" preserveAspectRatio="none" className="block w-full h-[50px] md:h-[70px]">
                <path d="M0,0 C480,0 480,80 960,80 C1200,80 1320,40 1440,0 L1440,0 L0,0 Z" fill="#0779BC" />
              </svg>
            </div>
          </section>

          {/* MARKETS SECTION */}
          <section ref={(el) => sectionRefs.current['markets'] = el} id="markets" className="relative py-16 md:py-20 bg-white overflow-hidden" data-testid="markets-section">

            <div className="max-w-7xl mx-auto px-6 lg:px-12">
              <div className="grid lg:grid-cols-2 gap-12 items-center">

                {/* Left — Content */}
                <div className="relative z-10">
                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                  >
                    <p className="text-[#0779BC] text-sm font-semibold uppercase tracking-widest mb-4">Our Markets</p>
                    <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 leading-tight mb-4">
                      Ecowater Is Expanding Across <span className="text-[#0779BC]">Africa</span>
                    </h2>
                    <p className="text-slate-500 text-sm leading-relaxed mb-8">
                      We are building a continental clean-water network across Sub-Saharan Africa, delivering safe, affordable, and plastic-free drinking water through smart refill systems.
                    </p>
                  </motion.div>

                  {/* Legend */}
                  <div className="flex gap-5 mb-6">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-sm bg-[#7CB342]" />
                      <span className="text-xs text-slate-600 font-medium">Operating</span>
                    </div>
                  </div>

                  {/* Country list */}
                  <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden max-w-xs">
                    <div className="max-h-[240px] overflow-y-auto">
                      <div className="p-4 space-y-1">
                        {[
                          { name: 'Tanzania', url: 'https://ecowater.co.tz', iso: 'tz' },
                          { name: 'Malawi', url: 'https://ecowatertech.mw', iso: 'mw' },
                          { name: 'Uganda', url: 'https://ecowateruganda.co.ug', iso: 'ug' },
                        ].map((country) => (
                          <div
                            key={country.name}
                            className="flex items-center gap-3 py-2.5 px-3 rounded-lg hover:bg-[#7CB342]/10 transition-colors group cursor-default"
                            data-testid={`market-${country.name.toLowerCase()}`}
                          >
                            <FlagImg iso={country.iso} size={22} />
                            <span className="font-semibold text-slate-800 text-sm flex-1">{country.name}</span>
                            <ArrowRight size={14} className="text-slate-300 group-hover:text-[#7CB342] group-hover:translate-x-1 transition-all" />
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  <p className="text-xs text-slate-400 mt-3 ml-1">
                    Click to see our work for each country
                  </p>
                </div>

                {/* Right — Country flag slideshow */}
                <motion.div
                  initial={{ opacity: 0, x: 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8 }}
                  className="relative overflow-hidden flex items-center justify-center"
                  style={{ height: '500px' }}
                  data-testid="world-map"
                >
                  <CountrySlideshow />
                </motion.div>
              </div>
            </div>
          </section>

          {/* IMPACT SECTION */}
          <section className="py-16 md:py-20 bg-slate-50 overflow-hidden" data-testid="impact-section">
            <div className="max-w-7xl mx-auto px-6 lg:px-12">
              {/* Heading */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-center mb-10"
              >
                <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight mb-4">
                  Our <span className="text-[#0779BC]">Impact</span>
                </h2>
                <p className="text-lg text-slate-500 max-w-xl mx-auto">
                  Every refill creates measurable change.
                </p>
              </motion.div>

              <div className="grid lg:grid-cols-2 gap-12 items-center">
                {/* LEFT — World map zoomed to Africa */}
                <motion.div
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8 }}
                  className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden relative"
                  style={{ height: '440px' }}
                  data-testid="impact-map"
                >
                  <iframe 
                    src="https://ecowatertrends.com/embed/topups-map.html?api_key=5jVFh-Cdpq8q7tyM33sT4JzFJj40fdpvQaUtn-8wdvo" 
                    width="100%" 
                    height="100%" 
                    frameBorder="0"
                    title="Ecowater Live Map"
                    style={{ border: 'none' }}
                    className="rounded-3xl"
                  />
                </motion.div>

                {/* RIGHT — Live metrics */}
                <motion.div
                  initial={{ opacity: 0, x: 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8 }}
                  className="flex flex-col gap-4"
                  data-testid="impact-metrics"
                >
                  <ImpactCounter />
                </motion.div>
              </div>
            </div>
          </section>

          {/* CONTACT SECTION */}
          <section ref={(el) => sectionRefs.current['contact'] = el} id="contact" className="relative py-8 md:py-10 bg-[#0779BC] overflow-hidden" data-testid="contact-section">
            {/* RIGHT — Full-height image */}
            <div className="hidden lg:block absolute right-0 top-0 bottom-0 w-[45%]">
              <img
                src="images/contact-bg.jpg"
                alt="Join The Network"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
              <div className="lg:w-1/2 lg:pr-12">

                {/* LEFT — Content */}
                <div>
                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="mb-4"
                  >
                    <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-2" data-testid="contact-title">Let's Talk</h2>
                    <p className="text-base text-white/80 leading-relaxed max-w-lg">
                      We're always looking to connect with those who share an interest in a sustainable future.
                    </p>
                  </motion.div>

                  {/* Contact info */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.15 }}
                    className="flex flex-col gap-2 mb-4"
                  >
                    <a href="mailto:hydrate@ecowaterinternational.com" className="group bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-4 flex items-center gap-4 hover:bg-white/20 transition-all" data-testid="contact-email-card">
                      <div className="w-11 h-11 bg-white/20 rounded-xl flex items-center justify-center shrink-0 group-hover:bg-white/30 transition-colors">
                        <Mail size={20} className="text-white" />
                      </div>
                      <p className="text-white font-medium text-sm">hydrate@ecowaterinternational.com</p>
                    </a>

                    <a href="tel:+255754510366" className="group bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-4 flex items-center gap-4 hover:bg-white/20 transition-all" data-testid="contact-phone-card">
                      <div className="w-11 h-11 bg-white/20 rounded-xl flex items-center justify-center shrink-0 group-hover:bg-white/30 transition-colors">
                        <Phone size={20} className="text-white" />
                      </div>
                      <p className="text-white font-medium text-sm">+255 754 510 366</p>
                    </a>

                    <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-4 flex items-center gap-4" data-testid="contact-location-card">
                      <div className="w-11 h-11 bg-white/20 rounded-xl flex items-center justify-center shrink-0">
                        <MapPin size={20} className="text-white" />
                      </div>
                      <p className="text-white font-medium text-sm">Dodoma, Tanzania</p>
                    </div>
                  </motion.div>

                  {/* CTA */}
                  <Button
                    onClick={() => { setContactFormContext('General Inquiry'); setShowContactForm(true); }}
                    className="bg-white text-[#0779BC] hover:bg-slate-100 rounded-full px-12 py-4 text-lg font-medium shadow-lg shadow-black/10"
                    data-testid="contact-get-in-touch-btn"
                  >
                    Get in Touch <ArrowRight size={18} className="ml-2 inline" />
                  </Button>
                </div>
              </div>
            </div>
          </section>
        </main>

        {/* FOOTER */}
        <footer className="bg-slate-900 text-white py-16">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="flex flex-col md:flex-row items-center justify-between gap-8">
              <img src={LOGO_URL} alt="Ecowater" className="h-8 brightness-0 invert" />
              
              <div className="flex flex-wrap justify-center gap-6">
                {navLinks.map((link) =>
                <button key={link.id} onClick={() => scrollToSection(link.id)} className="text-slate-400 hover:text-white text-sm">
                    {link.name}
                  </button>
                )}
                <a href="https://www.ecowaterfoundation.org" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-white text-sm">
                  Ecowater Foundation
                </a>
              </div>
            </div>
            
            <div className="mt-12 pt-8 border-t border-slate-800 text-center text-slate-500 text-sm">
              © {new Date().getFullYear()} Ecowater. All rights reserved.
            </div>
          </div>
        </footer>

        <Toaster position="top-right" richColors />

        {/* CONTACT FORM POPUP MODAL */}
        <AnimatePresence>
          {showContactForm && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
              onClick={() => setShowContactForm(false)}
              data-testid="contact-modal-overlay"
            >
              <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                transition={{ duration: 0.25 }}
                className="relative bg-white rounded-3xl p-8 md:p-10 shadow-2xl w-full max-w-lg"
                onClick={(e) => e.stopPropagation()}
                data-testid="contact-modal"
              >
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-2xl font-bold text-slate-900">Get in Touch</h3>
                    <p className="text-slate-500 text-sm mt-1">
                      {contactFormContext ? `Interest: ${contactFormContext}` : "We'll get back to you shortly"}
                    </p>
                  </div>
                  <button
                    onClick={() => setShowContactForm(false)}
                    className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors"
                    data-testid="contact-modal-close"
                  >
                    <X size={16} className="text-slate-500" />
                  </button>
                </div>
                <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-700 mb-1.5 block text-sm">Name</Label>
                      <Input
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                        className="bg-slate-50 border-slate-200 rounded-xl"
                        placeholder="Your name"
                        data-testid="input-name"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-700 mb-1.5 block text-sm">Email</Label>
                      <Input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required
                        className="bg-slate-50 border-slate-200 rounded-xl"
                        placeholder="you@example.com"
                        data-testid="input-email"
                      />
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-700 mb-1.5 block text-sm">Phone Number</Label>
                      <div className="flex gap-2">
                        <div className="w-28 shrink-0">
                          <CountrySelect
                            value={formData.phoneCountry}
                            onChange={(v) => setFormData({ ...formData, phoneCountry: v })}
                            label=""
                            showCode
                            placeholder="+code"
                          />
                        </div>
                        <Input
                          type="tel"
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          required
                          className="bg-slate-50 border-slate-200 rounded-xl flex-1"
                          placeholder="xxx xxx xxx"
                          data-testid="input-phone"
                        />
                      </div>
                    </div>
                    <CountrySelect
                      value={formData.country}
                      onChange={(v) => setFormData({ ...formData, country: v })}
                      label="Country"
                      showCode={false}
                      placeholder="Select country"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-700 mb-1.5 block text-sm">Message</Label>
                    <Textarea
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      rows={3}
                      className="bg-slate-50 border-slate-200 rounded-xl resize-none"
                      placeholder="Tell us about yourself and your interest..."
                      data-testid="input-message"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-[#0779BC] hover:bg-[#06649b] text-white rounded-full py-3 text-base font-medium"
                    data-testid="submit-btn"
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </Button>
                </form>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </BrowserRouter>
    </div>);

}

export default App;